// import { createBrowserInspector } from '@statelyai/inspect';
import { useConstant } from '@wkda/funnel-components';
import { useActorRef } from '@xstate/react';
import { useCacheStorage } from '../../../formCache';
import { useCarleadConfiguration } from '../../../hooks/useCarleadCreationConfiguration';
import { useCarleadFieldChangedTracking } from '../../../hooks/useCarleadFieldChangedTracking';
import { createFieldCategories, flowToArray, generateConfigForNormalMachine, transformInitialFlow } from '../../helpers/parseFlowToMachine';
import { createNormalFlowMachineConfig } from '../normalFlowMachineConfig';
import { pushSelectEventToDataLayerAction, questionnaire } from '../shared/actions';
import { useSplitFieldsMachineServices } from './services';

/**
 * How to debug a machine:
 * - install @statelyai/inspect
 * - uncomment the import of createBrowserInspector
 * - uncomment the inspector variable
 * - add the inspector to the useActorRef call
 * Remember to remove the inspector before committing the code
 */

// const inspector =
//   typeof window !== 'undefined' ? createBrowserInspector() : undefined;

export function useSplitFieldsFlowService(_flow) {
  const flow = transformInitialFlow(_flow);
  const flowArray = flowToArray(_flow);
  const flowCategories = createFieldCategories(_flow);
  const {
    syncQuestionnaireFieldsToCookie
  } = useCarleadConfiguration();
  const generatedStatesObjects = generateConfigForNormalMachine(flow, flowArray);
  const cache = useCacheStorage();
  const trackLeadFieldChanged = useCarleadFieldChangedTracking();
  const formMachine = createNormalFlowMachineConfig(generatedStatesObjects);
  const configuredMachine = formMachine.provide({
    actions: {
      // action used for general SELECT action from user
      // and prefill
      pushDataLayerEvent(args) {
        pushSelectEventToDataLayerAction(args, trackLeadFieldChanged);
      },
      // action only used for questionnaire fields data preload
      pushToDataLayerCache(_, params) {
        trackLeadFieldChanged(params.fieldName, 'cache');
      },
      pushQuestionnaireDataLayerEvent: questionnaire.pushDataLayerEvent(trackLeadFieldChanged),
      pushQuestionToDataLayerCache: questionnaire.pushToDataLayerCache(trackLeadFieldChanged),
      syncQuestionnaireCookies: (args, params) => {
        questionnaire.syncAnswerToCookie(syncQuestionnaireFieldsToCookie, args, params);
      }
    },
    actors: {
      ...useSplitFieldsMachineServices()
    }
  });
  const finalMachine = useConstant(() => configuredMachine);
  const actorRef = useActorRef(finalMachine, {
    // inspect: inspector?.inspect,
    input: {
      dataFields: flowCategories.dataField ?? [],
      otherFields: flowCategories.normalField ?? [],
      questionnaireFields: flowCategories.questionnaireField ?? [],
      // DSB preload support
      // Feed with values object from dsb or empty
      // object if there is none or data does not match
      preloadedData: cache.getData()
    }
  });
  return actorRef;
}